import ky from 'ky'
import { canAccessLocalStorage } from '../assertion'
import {
  BASE_URL,
  DEBUG_MODE_STORAGE_KEY,
  WEB_STORAGE_PREFIX_KEYS,
  __DEV__
} from '../constants'
import {
  getExtensionVersion,
  getStorage,
  isInExtension,
  removeFromStorage,
  setStorage
} from '../extension'
import { log } from '../misc'
import { getNetworkStatus } from '../network'
import type {
  DiagnosticsDispatcherArgs,
  DiagnosticsExtensionStorage,
  DiagnosticsRecordArgs,
  DiagnosticsStorageHydration,
  DiagnosticsStorageKey,
  UserInfo
} from './types'

export const apiClient = ky.extend({
  prefixUrl: BASE_URL
})

class DiagnosticsClass {
  version: number = 1
  buffer: Record<string, any>[] = []
  maxBufferSize: number = 2000
  storageKey: DiagnosticsStorageKey = '__DIAGNOSTICS__'
  bufferVersionKey: string = 'mote-buffer-version'
  // This should allow us to hydrate the buffer from storage in background.js
  persistTimeout: NodeJS.Timeout | undefined
  hasLocalStorageAccess: boolean | Promise<boolean> = false

  constructor() {
    this.init()
  }

  private async init() {
    let hasLocalStorageAccess = false
    try {
      hasLocalStorageAccess = await canAccessLocalStorage()

      if (isInExtension() && hasLocalStorageAccess) {
        await removeFromStorage(this.storageKey, DEBUG_MODE_STORAGE_KEY)
      }
    } catch {}

    this.hasLocalStorageAccess = hasLocalStorageAccess

    this.checkBufferVersion().then(() =>
      this.hydrateFromStorage()
        .then(results => {
          if (isInExtension()) {
            this.buffer =
              results &&
              this.storageKey in (results as DiagnosticsExtensionStorage)
                ? (results as DiagnosticsExtensionStorage)[this.storageKey]
                : []
          } else {
            this.buffer = results ? JSON.parse(results as string) : []
          }
          this.normalizeBuffer()
        })
        .catch(err => log(err.message))
    )
  }

  async record({
    message,
    metaData = {},
    level = 'info'
  }: DiagnosticsRecordArgs): Promise<void> {
    if (isInExtension()) return
    if (!message) throw new Error('Event message must be provided')

    await this.normalizeBuffer()

    if (await this.isAnonymousUser()) return

    this.buffer.push({
      timestamp: Date.now(),
      level,
      message,
      metaData
    })

    log(`Mote diagnostics: ${message}`)

    if (this.persistTimeout) {
      clearTimeout(this.persistTimeout)
    }

    this.persistTimeout = setTimeout(this.hydrateStorage.bind(this), 1000)
  }

  async dispatch({
    userVisibleErrorMessage,
    dispatchContext,
    title
  }: DiagnosticsDispatcherArgs): Promise<void> {
    const inDebugMode = await this.inDebugMode()
    const randomChance = Math.floor(Math.random() * 1000) + 1
    const shouldRandomlyDispatch = randomChance === 1

    if (!shouldRandomlyDispatch && !inDebugMode) return

    if (await this.isAnonymousUser()) return

    const payload = {
      userInfo: await this.getUserInfo(),
      clientInfo: {
        clientType: isInExtension() ? 'extension' : 'web',
        extensionVersion: getExtensionVersion(),
        userAgent: window.navigator?.userAgent
      },
      pageContext: window.location.href,
      userVisibleErrorMessage,
      dispatchContext,
      title,
      events: this.buffer,
      networkStatus: getNetworkStatus()
    }

    apiClient
      .extend({
        timeout: 30000,
        retry: {
          limit: 3,
          methods: ['post']
        }
      })
      .post('s/diagnostics', {
        json: payload
      })
      .text()
      .then(_ => {
        if (__DEV__) return
        if (this.persistTimeout) {
          clearTimeout(this.persistTimeout)
        }
        this.buffer = []
        this.hydrateStorage()
      })
      .catch(err => console.error('error', err.message))

    if (isInExtension() && inDebugMode) {
      this.snapshotDOM({
        userInfo: payload.userInfo,
        title: payload.title,
        pageContext: payload.pageContext
      })
    }
  }

  async normalizeBuffer(): Promise<void> {
    if (this.buffer.length >= this.maxBufferSize) {
      log('normalizeBuffer - before', this.buffer.length)
      this.buffer.splice(0, this.buffer.length - this.maxBufferSize + 1000)
      await this.removeFromStorage()
      this.hydrateStorage()
      log('normalizeBuffer - after', this.buffer.length)
    }
  }

  async checkBufferVersion(): Promise<void> {
    if (!this.hasLocalStorageAccess) return

    const currentVersion = isInExtension()
      ? (await getStorage(this.bufferVersionKey))?.[this.bufferVersionKey]
      : window.localStorage.getItem(this.bufferVersionKey)

    if (!currentVersion || currentVersion < this.version) {
      this.buffer = []
      this.hydrateStorage()
      this.updateBufferVersion()
    }
  }

  snapshotDOM(additionalPayload: Record<string, any> = {}): void {
    apiClient
      .extend({
        timeout: 30000,
        retry: {
          limit: 3,
          methods: ['post']
        }
      })
      .post('s/snapshot', {
        json: {
          ...additionalPayload,
          snapshot: new XMLSerializer().serializeToString(document)
        }
      })
      .text()
      .catch(err => console.error('error', err.message))
  }

  updateBufferVersion(): void | ReturnType<typeof setStorage> {
    if (!this.hasLocalStorageAccess) return

    return isInExtension()
      ? setStorage({ [this.bufferVersionKey]: this.version })
      : window.localStorage.setItem(
          this.bufferVersionKey,
          JSON.stringify(this.version)
        )
  }

  async inDebugMode(): Promise<boolean> {
    if (!isInExtension()) return true
    if (!this.hasLocalStorageAccess) return false

    return Boolean(
      (await getStorage(DEBUG_MODE_STORAGE_KEY))?.[DEBUG_MODE_STORAGE_KEY]
    )
  }

  async getUserInfo(): Promise<UserInfo> {
    if (!this.hasLocalStorageAccess)
      return {
        userId: 'unknown',
        primaryEmailAddress: null,
        name: null,
        isMinor: null
      }

    if (isInExtension()) {
      return {
        userId: (await getStorage('userId'))?.userId || 'not-found',
        primaryEmailAddress:
          (await getStorage('primaryEmailAddress'))?.primaryEmailAddress ||
          null,
        name: (await getStorage('name'))?.name || null,
        isMinor: (await getStorage('isMinor'))?.isMinor || null
      }
    }

    return {
      userId:
        window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}userId`) ||
        'not-found',
      primaryEmailAddress:
        window.localStorage.getItem(
          `${WEB_STORAGE_PREFIX_KEYS}primaryEmailAddress`
        ) || null,
      name:
        window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}name`) || null,
      isMinor:
        window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}isMinor`) || null
    }
  }

  async isAnonymousUser(): Promise<boolean> {
    if (!this.hasLocalStorageAccess) return true

    let userId

    if (isInExtension()) {
      userId = (await getStorage('userId'))?.userId
    } else {
      userId = window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}userId`)
    }

    return !Boolean(userId)
  }

  hydrateStorage(): void | ReturnType<typeof setStorage> {
    if (!this.hasLocalStorageAccess) return

    return isInExtension()
      ? setStorage({ [this.storageKey]: this.buffer })
      : window.localStorage.setItem(
          this.storageKey,
          JSON.stringify(this.buffer)
        )
  }

  async removeFromStorage(): Promise<void> {
    if (!this.hasLocalStorageAccess) return

    return isInExtension()
      ? await removeFromStorage(this.storageKey)
      : window.localStorage.removeItem(this.storageKey)
  }

  async hydrateFromStorage(): Promise<DiagnosticsStorageHydration> {
    if (!this.hasLocalStorageAccess) return null

    return (
      isInExtension()
        ? await getStorage(this.storageKey)
        : window.localStorage.getItem(this.storageKey)
    ) as Promise<DiagnosticsStorageHydration>
  }
}

export const Diagnostics = new DiagnosticsClass()
